import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { IconKey } from '../../../../whitelabel/icon-key.enum';
import { LanguageService } from '../../../core/services/language.service';
import { ButtonComponent } from '../../../osp-ui/components/button/button/button.component';
import { CardOptionListComponent } from '../../../osp-ui/components/form/card-option-list/card-option-list.component';
import { CardOptionType } from '../../../osp-ui/components/form/card-option-list/contracts/card-option-type.contract';
import { DialogComponent } from '../../../osp-ui/components/overlay/dialog/dialog/dialog.component';
import { OspTemplateDirective } from '../../../osp-ui/directives/osp-template.directive';
import { IconProviderService } from '../../../whitelabel/services/icon-provider.service';
import { AssetType } from '../../enums/asset-type.enum';
import { IconPipe } from '../../pipes/icon.pipe';
import { AccessService } from '../../services/access.service';

@Component({
  selector: 'osp-entity-create-dialog',
  standalone: true,
  imports: [
    ButtonComponent,
    DialogComponent,
    OspTemplateDirective,
    CardOptionListComponent,
    FormsModule,
    IconPipe,
    TranslateModule,
  ],
  templateUrl: './entity-create-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityCreateDialogComponent {
  private readonly accessService = inject(AccessService);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly languageService = inject(LanguageService);
  private readonly iconProviderService = inject(IconProviderService);

  readonly assets: CardOptionType[] = [
    {
      type: AssetType.EQUIPMENT,
      icon: this.iconProviderService.getIcon(IconKey.EquipmentCreateMain),
      label: this.languageService.getInstant('general.equipm.s'),
      description: this.languageService.getInstant('shared.createEntity.entitySelection.equipment.description'),
      hidden: !this.accessService.canCreateEquipment(),
    },
    {
      type: AssetType.PROJECT,
      icon: this.iconProviderService.getIcon(IconKey.ProjectCreateMain),
      label: this.languageService.getInstant('general.project.s'),
      description: this.languageService.getInstant('shared.createEntity.entitySelection.project.description'),
      hidden: !this.accessService.canCreateProject(),
    },
    {
      type: AssetType.STOCK,
      icon: this.iconProviderService.getIcon(IconKey.StockCreateMain),
      label: this.languageService.getInstant('general.stock.s'),
      description: this.languageService.getInstant('shared.createEntity.entitySelection.stock.description'),
      hidden: !this.accessService.canCreateStock(),
    },
    {
      type: AssetType.CONTACT,
      icon: this.iconProviderService.getIcon(IconKey.ContactTypeGeneric),
      label: this.languageService.getInstant('modules.contact.base.contact.s'),
      description: this.languageService.getInstant('shared.createEntity.entitySelection.contact.description'),
      hidden: !this.accessService.canManageContact(),
    },
  ];
  readonly $selectedType = signal<AssetType | null>(null);
  readonly IconKey = IconKey;

  protected onCancel(): void {
    this.dialogRef.close();
  }

  protected onNext(): void {
    const selectedType = this.$selectedType();
    if (selectedType) {
      this.dialogRef.close(selectedType);
    }
  }
}
